import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, MapPinIcon, PhoneIcon, ShieldCheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { CONTACT, HERO_IMAGE } from '../data/media';
import { Mascot } from './Mascot';
export function Hero() {
  const {
    t
  } = useLanguage();
  const reduced = useReducedMotion();
  const rise = (delay: number) => reduced ? {} : {
    initial: {
      opacity: 0,
      y: 20
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.3,
      delay,
      ease: [0.23, 1, 0.32, 1] as const
    }
  };
  return <section id="top" className="relative w-full bg-ink pt-[72px]" data-id="element-155" data-mp="s9q8brns6vfz">
      <div className="relative min-h-[560px] w-full overflow-hidden lg:min-h-[660px]" data-id="element-156" data-mp="nczrv382dxu1">
        <img src={HERO_IMAGE} alt="Completed custom home built by Pollo Construction & Granite" className="absolute inset-0 h-full w-full object-cover" data-id="element-157" data-mp="3d21t5zvq8oos" />
        <div className="absolute inset-0" style={{
        background: 'linear-gradient(180deg, rgba(26,26,26,0.55) 0%, rgba(26,26,26,0.65) 38%, rgba(26,26,26,0.94) 100%)'
      }} aria-hidden="true" data-id="element-158" data-mp="3j8sgk46tfprf" />

        <div className="relative mx-auto flex min-h-[560px] max-w-shell flex-col justify-end px-4 pb-14 pt-20 sm:px-6 lg:min-h-[660px] lg:px-8 lg:pb-20" data-id="element-159" data-mp="39cfmh57cz9w5">
          <motion.p {...rise(0)} className="mb-4 flex items-center gap-3 font-display text-xs font-bold uppercase tracking-[0.24em] text-gold-500" data-id="element-160" data-mp="30hrjjlp9a4ai">
            <Mascot size={34} tone="gold" animate data-id="element-161" data-mp="2qkdba9dhklta" />
            {t.hero.eyebrow}
          </motion.p>

          <motion.h1 {...rise(0.06)} className="max-w-3xl font-display text-[34px] font-extrabold leading-[1.05] text-white sm:text-5xl lg:text-[60px]" data-id="element-162" data-mp="2vievixmner6c">
            {t.hero.headline}{' '}
            <span className="text-brand-500" data-id="element-163" data-mp="2ljgyf6b5ppbq">{t.hero.headlineAccent}</span>
          </motion.h1>

          <motion.p {...rise(0.12)} className="mt-5 max-w-xl text-lg text-white/75 lg:text-2xl" data-id="element-164" data-mp="s2te9nr9cdqs">
            {t.hero.sub}
          </motion.p>

          <motion.p {...rise(0.15)} className="mt-4 font-display text-sm font-bold uppercase tracking-wider text-gold-500" data-id="element-165" data-mp="n9i2d87l96s0">
            {t.hero.locationLine}
          </motion.p>

          <motion.ul {...rise(0.18)} className="mt-7 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm font-semibold text-white/85" data-id="element-166" data-mp="1b0njrrt6g74f">
            {t.hero.badges.map((badge, index) => <li key={badge} className="flex items-center gap-3" data-id="element-167" data-mp="111yb8pn67z0u" data-mpi={index}>
                {index > 0 ? <span className="h-1.5 w-1.5 rounded-full bg-brand-500" aria-hidden="true" data-id="element-168" data-mp="x1wbim0zxfo7" /> : <ShieldCheckIcon className="h-4 w-4 text-brand-500" aria-hidden="true" data-id="element-169" data-mp="3ltqclcptivti" />}
                {badge}
              </li>)}
          </motion.ul>

          <motion.div {...rise(0.24)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:items-center" data-id="element-170" data-mp="1xacgp6hknput">
            <a href="#contact" className="group inline-flex items-center justify-center gap-2 rounded bg-brand-500 px-7 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-ink transition-colors duration-200 ease-smooth hover:bg-brand-400" data-id="element-171" data-mp="21bxwp0pekk98">
              {t.hero.primary}
              <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-smooth group-hover:translate-x-1" aria-hidden="true" data-id="element-172" data-mp="2g1wvn5os7zpr" />
            </a>
            <a href="#projects" className="inline-flex items-center justify-center rounded border-2 border-white/70 px-7 py-4 font-display text-sm font-extrabold uppercase tracking-wide text-white transition-colors duration-200 ease-smooth hover:border-brand-500 hover:text-brand-500" data-id="element-173" data-mp="2oye606pbntkg">
              {t.hero.secondary}
            </a>
          </motion.div>

          <motion.div {...rise(0.3)} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-white/15 pt-6" data-id="element-174" data-mp="4dgxctsz5l7l">
            <a href={`tel:+1${CONTACT.phoneRaw}`} className="inline-flex items-center gap-2 font-display text-xl font-extrabold text-white transition-colors duration-200 ease-smooth hover:text-brand-500" data-id="element-175" data-mp="e88w40yame3y">
              <PhoneIcon className="h-5 w-5 text-brand-500" aria-hidden="true" data-id="element-176" data-mp="3cr7evz46dt7s" />
              {CONTACT.phoneDisplay}
            </a>
            <span className="inline-flex items-center gap-2 rounded bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-white/85" data-id="element-177" data-mp="3rgbwbe35bteu">
              <MapPinIcon className="h-4 w-4 text-brand-500" aria-hidden="true" data-id="element-178" data-mp="3mnagn3v47pu7" />
              {t.hero.serving}
            </span>
            <span className="text-xs font-bold uppercase tracking-wider text-white/50" data-id="element-179" data-mp="2u0y5a4626z62">
              {CONTACT.license}
            </span>
          </motion.div>
        </div>
      </div>
    </section>;
}